/**
 * Parsing money typed by a human.
 *
 * The app stores cents as integers and shows dollars, so every editable money
 * field converts at the boundary. Doing that inline in a view means the same
 * five edge cases get re-decided per field — and a `Number('')` returning 0
 * silently turns "I cleared this" into "I set it to zero", which for a budget
 * target is the difference between "no target" and "a target of $0".
 */

/**
 * Dollars as typed → integer cents, or null.
 *
 * Null means "no value": an empty field, or input that isn't a usable amount.
 * Callers decide whether null means "clear it" or "leave it alone" — the two
 * differ by field, so this function refuses to guess.
 */
/**
 * Scale a decimal string by 10^places, half-up, without float drift.
 *
 * `Math.round(Number(x) * 100)` is NOT safe for money: the float nearest to
 * "1.005" is slightly below it, so it rounds DOWN to 100, while "10.005" lands
 * slightly above and rounds UP to 1001. Same input shape, different answer.
 * Reading the digits as text sidesteps the representation entirely.
 *
 * Returns null for anything that isn't a plain non-negative decimal — no sign,
 * no exponent, no "Infinity".
 */
export function scaleDecimal(raw: string, places: number): number | null {
  const t = raw.trim().replace(/[$,\s]/g, '');
  if (t === '') return null;
  const m = /^(\d+)?(?:\.(\d*))?$/.exec(t);
  if (!m || (m[1] === undefined && !m[2])) return null;

  const whole = m[1] ?? '0';
  // One digit past what we keep, to decide the rounding.
  const frac = (m[2] ?? '').padEnd(places + 1, '0');
  const kept = frac.slice(0, places) || '0';
  const next = Number(frac[places]);

  const scaled = Number(whole) * 10 ** places + Number(kept);
  if (!Number.isSafeInteger(scaled)) return null;
  return next >= 5 ? scaled + 1 : scaled;
}

/**
 * Dollars as typed → integer cents, or null.
 *
 * Null means "no value": an empty field, or input that isn't a usable amount.
 * Callers decide whether null means "clear it" or "leave it alone" — the two
 * differ by field, so this function refuses to guess.
 */
export function dollarsToCents(raw: string): number | null {
  return scaleDecimal(raw, 2);
}

/** Cents → the plain number a dollar input should show. '' when unset. */
export function centsToDollarInput(cents: number | null | undefined): string {
  if (cents == null || cents === 0) return '';
  return String(Math.round(cents / 100));
}
